import { useMutation } from "@tanstack/react-query";
import uploadSelfie from "../../../../services/uploadSelfie";
import React, { useEffect, useState } from "react";
import CaptureSelfie from "./captureSelfie";
import SelfieResult from "./selfieResult";
import Loader from "../../../../utils/loader";
import btnStyle from "../../../../utils/buttonStyle.json"
import FeedbackModal from "../../../../utils/feedbackModal";
import { IoIosArrowRoundBack } from "react-icons/io";
import FaceScanner from "../../../../utils/mediaPipeLandmarks";

type SelfieProps = {
    question: {
        advanceSelfieLoadingText: string;
        helpText: string;
        selfieResultText: string;
        selfieResultBtns: object;
        type: string;
        _id: string;
        imgUrl: string;
        questionText: string;
        cta: {
            _id: string;
            ctaText: string;
            ctaType: string;
            linkedQuestionId: string;
        }[];
    };
    updateUserResponse: (response: {
        questionType: string;
        questionText: string;
        nextQuestionId: string;
        _id: string;
        action?: string;
        answerResponse: Array<object>;
        selfieResponse: Array<object>;
        selfieID: string;
        userSelfie: string;
        timeStamp: number;
    }) => void;
    responseData: {
        questionType: string;
        questionText: string;
        nextQuestionId: string;
        _id: string;
        action?: string;
        answerResponse: Array<object>;
        selfieResponse: Array<object>;
        selfieID: string;
        userSelfie: string;
        timeStamp: number;
    };
    handleBackBtn: () => void;
    backBtnConfig: object;
    saveAppEvent: () => void;
    questionIndex: number;
    settings: {
        selfieAdvanceMap: boolean;
        selfieAnimation: boolean;
    };
};

const Selfie: React.FC<SelfieProps> = ({ question, updateUserResponse, responseData, handleBackBtn, saveAppEvent, questionIndex, backBtnConfig, settings }) => {
    const [isCapture, setCapture] = useState(false);
    const [selfieResult, setSelfieResult] = useState(!!responseData?.userSelfie);
    const [userSelfie, setUserSelfie] = useState(responseData?.userSelfie || null);
    const [showError, setShowError] = useState<boolean>(false);
    const advancedSelfie = settings?.selfieAnimation ?? false;

    const { mutate, data, error, isPending, isError } = useMutation({
        mutationKey: ["uploadSelfie"],
        mutationFn: ({ userPhoto }: { userPhoto: string }) => uploadSelfie({
            user: "mango",
            imageBS64: userPhoto,
            essential_only: true,
            facemap: settings?.selfieAdvanceMap ?? false
        }),
        retry: false,
        onSuccess: (data) => {
            setCapture(false);
            setSelfieResult(true);
            saveAppEvent("submit_selfie", { value: 1 }, questionIndex)
            saveAppEvent("selfie_response", data, questionIndex)
        },
        onError: () => {
            setShowError(true);
            saveAppEvent("submit_selfie", { value: 0 }, questionIndex)
        }
    });

    useEffect(() => {
        if (responseData?.userSelfie) {
            setUserSelfie(responseData.userSelfie);
        }
    }, [responseData]);

    const handleRetry = () => {
        setCapture(false);
        setSelfieResult(false);
        setUserSelfie(null);
    };
    const constructResponse = (cta: { _id: string; ctaText: string; ctaType: string; linkedQuestionId: string; }) => {
        const isSkip = cta?.ctaType === "skip";
        return {
            questionType: question.type,
            questionText: question.questionText,
            nextQuestionId: cta?.linkedQuestionId || "",
            _id: question._id,
            action: cta?.ctaType,
            answerResponse: [], // Add the missing answerResponse property
            selfieResponse: isSkip ? [] : data?.response || responseData?.selfieResponse,
            selfieID: isSkip ? "" : data?.id || responseData?.selfieID,
            userSelfie: isSkip ? "" : userSelfie || responseData?.userSelfie,
            timeStamp: new Date().getTime()
        };
    };

    const handleSubmitBtn = () => {
        updateUserResponse(constructResponse(question.cta[1]));
    };

    const handleSelfieUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
                setUserSelfie(reader.result as string); // Cast reader.result to string
                mutate({ userPhoto: reader.result as string }); // Cast reader.result to string
            };
        }
        e.target.value = "";
    };

    const handleBtnClick = (cta: { _id: string; ctaText: string; ctaType: string; linkedQuestionId: string; }) => {
        if (cta.ctaType === "upload") {
            document.getElementById("selfie-upload")?.click();
        } else if (cta.ctaType === "capture") {
            setCapture(true);
        } else if (cta.ctaType === "skip") {
            updateUserResponse(constructResponse(cta));
        }
    };

    if (isPending && advancedSelfie && userSelfie) {
        return <FaceScanner imageSrc={userSelfie} loadingText={question?.advanceSelfieLoadingText} />;
    }

    if (selfieResult) {
        return <SelfieResult selfieResultText={question.selfieResultText} selfieResultBtns={question?.selfieResultBtns} selfieImg={userSelfie ?? ''} handleRetry={handleRetry} handleSubmitBtn={handleSubmitBtn} />;
    }

    if (isCapture) {
        return <CaptureSelfie setUserSelfie={setUserSelfie} isPending={isPending} mutate={mutate} handleRetry={handleRetry} backBtnConfig={backBtnConfig} />;
    }

    return (
        <>
            <button className="tangent_backbtn_custom tangent_backbtn_mob_custom tngnt-absolute tngnt-top-0 tngnt-self-start tngnt-m-4 tngnt-text-[30px]" onClick={handleBackBtn}>
                {backBtnConfig ? <img src={backBtnConfig.backBtn_icon} alt="back button" /> : <IoIosArrowRoundBack color="#929292" size={"3rem"} />}
            </button>
            <div className={"tangent_selfie_screen_custom tangent_selfie_screen_mob_custom tngnt-flex tngnt-flex-col tngnt-items-center tngnt-gap-4 sm:tngnt-gap-6 tngnt-p-3 tngnt-pt-16 sm:tngnt-p-6 sm:tngnt-pt-16 tngnt-justify-center tngnt-w-[100%]"} id={`comp_${question._id}`}>
                {question.imgUrl && <img className="tangent_selfie_image_custom tangent_selfie_image_mob_custom tngnt-max-h-[300px] sm:tngnt-w-[60%] tngnt-w-[80%] sm:tngnt-max-h-[70vh] tngnt-object-contain" src={question.imgUrl} alt="question image" />}
                <div className="tangent_global_style tangent_global_style_custom tangent_question_text tangent_question_text_custom  tangent_selfie_text_custom tangent_selfie_text_mob_custom tngnt-text-center tngnt-text-[20px] tngnt-ml-[15px] tngnt-mr-[15px] tngnt-w-[90%]" dangerouslySetInnerHTML={{ __html: question.questionText }} />
                <div className="tngnt-flex tngnt-justify-center tngnt-items-center tngnt-gap-6 sm:tngnt-w-[90%] tngnt-w-[100%] tngnt-align-middle tngnt-flex-wrap tngnt-flex-row">
                    <input type="file" id="selfie-upload" className="tngnt-hidden" onChange={handleSelfieUpload} />
                    {question.cta.map((cta) => (
                        <button
                            key={cta._id}
                            className={(cta.ctaType === "skip" ? "tangent_global_style tangent_global_style_custom tangent_selfie_skip_button_custom tangent_selfie_skip_button_mob_custom " : "") + btnStyle[cta.ctaType] + " tangent_global_style tngnt-p-2 tngnt-w-[40%] sm:tngnt-w-[35%] "}
                            onClick={() => handleBtnClick(cta)}
                            disabled={isPending}
                        >
                            {cta.ctaText}
                        </button>
                    ))}
                </div>
                {
                    isPending && <Loader />
                }
                {
                    showError && <FeedbackModal message={`Error: ${error.description}`} toggleShow={() => setShowError(false)} />
                }
                {
                    question?.helpText && <div className="tangent_global_style tangent_global_style_custom tangent_helpText_custom tngnt-text-center tngnt-p-2 tngnt-mt-6 tngnt-text-[14px] tngnt-leading-[20px]" dangerouslySetInnerHTML={{ __html: question.helpText }}></div>
                }
            </div >
        </>
    );
};

export default Selfie;

//#888b93